import { z } from 'zod';
import { FREIGHT_TYPE_SCHEMA, DISPOSAL_PURPOSE_SCHEMA } from '@schuettflix/interfaces';
import { LocationConfigurationSchema } from './location-configuration.ts';
import { VehicleClassSchema } from './vehicle-class.ts';
import { AdditionalPropertiesIdSchema } from '../enums/additional-properties-schema-id.ts';
import { AbstractAdditionalProperties } from './abstract-additional-properties.ts';
import { ProcessedBySchema } from '../enums/processed-by.ts';
import { LocationTypeSchema } from '@/modules/location/domain/LocationSchema.ts';
import { DispositionFormDataSchema } from '@/modules/disposition/DispositionConfigurationPanel.tsx';

export const AutomaticTransportPropertiesSchemaV1 = z.object({
    ...AbstractAdditionalProperties.shape,
    schemaId: AdditionalPropertiesIdSchema.extract(['AUTOMATIC_TRANSPORT_V1']),
    freightType: FREIGHT_TYPE_SCHEMA.exclude(['HAZARDOUS_WASTE', 'NON_HAZARDOUS_WASTE']),
    locations: z.array(LocationConfigurationSchema),
    vehicleClass: VehicleClassSchema.omit({ payloadInTons: true }),
    weighingLocation: LocationTypeSchema.nullable(),
    // NOTE: disposition AND numberOfTransports here are optional as we are using this
    // schema for ORDER and POSITION, and both are only relevant for orders
    disposition: DispositionFormDataSchema.optional(),
    numberOfTransports: z.number().positive().optional(),
    processedBy: ProcessedBySchema,
});

export type AutomaticTransportPropertiesV1 = z.infer<typeof AutomaticTransportPropertiesSchemaV1>;

export const AutomaticTransportHazardousWastePropertiesSchemaV1 = z.object({
    ...AbstractAdditionalProperties.shape,
    schemaId: AdditionalPropertiesIdSchema.extract(['AUTOMATIC_HAZARDOUS_WASTE_TRANSPORT_V1']),
    freightType: FREIGHT_TYPE_SCHEMA.extract(['HAZARDOUS_WASTE']),
    disposalPurpose: DISPOSAL_PURPOSE_SCHEMA,
    disposalCertificateNumber: z.string().optional(),
    locations: z.array(LocationConfigurationSchema),
    vehicleClass: VehicleClassSchema.omit({ payloadInTons: true }),
    weighingLocation: LocationTypeSchema.nullable(),
    // NOTE: disposition AND numberOfTransports here are optional as we are using this
    // schema for ORDER and POSITION, and both are only relevant for orders
    disposition: DispositionFormDataSchema.optional(),
    numberOfTransports: z.number().positive().optional(),
    processedBy: ProcessedBySchema,
});

export type AutomaticTransportHazardousWastePropertiesV1 = z.infer<
    typeof AutomaticTransportHazardousWastePropertiesSchemaV1
>;

export const AutomaticTransportNonHazardousWastePropertiesSchemaV1 = z.object({
    ...AbstractAdditionalProperties.shape,
    schemaId: AdditionalPropertiesIdSchema.extract(['AUTOMATIC_NON_HAZARDOUS_WASTE_TRANSPORT_V1']),
    freightType: FREIGHT_TYPE_SCHEMA.extract(['NON_HAZARDOUS_WASTE']),
    disposalPurpose: DISPOSAL_PURPOSE_SCHEMA,
    locations: z.array(LocationConfigurationSchema),
    vehicleClass: VehicleClassSchema.omit({ payloadInTons: true }),
    weighingLocation: LocationTypeSchema.nullable(),
    // NOTE: disposition AND numberOfTransports here are optional as we are using this
    // schema for ORDER and POSITION, and both are only relevant for orders
    disposition: DispositionFormDataSchema.optional(),
    numberOfTransports: z.number().positive().optional(),
    processedBy: ProcessedBySchema,
});

export type AutomaticTransportNonHazardousWastePropertiesV1 = z.infer<
    typeof AutomaticTransportNonHazardousWastePropertiesSchemaV1
>;
